import {
  onBeforeUnmount,
  onMounted,
  watch,
} from 'vue'
import type { Ref } from 'vue'

const REVIEW_OBJECT_TYPES = {
  doctor: 'doctor',
  lpu: 'lpu',
} as const

/**
 * Просмотры копятся на уровне модуля, а не инстанса списка:
 * повторный mount после раскрытия «ещё отзывы» не должен слать те же id.
 */
const VIEWED_IDS: Record<string, Set<number>> = {
  [REVIEW_OBJECT_TYPES.doctor]: new Set(),
  [REVIEW_OBJECT_TYPES.lpu]: new Set(),
}

const SENT_IDS: Record<string, number[]> = {
  [REVIEW_OBJECT_TYPES.lpu]: [],
  [REVIEW_OBJECT_TYPES.doctor]: [],
}

const SEND_VIEWED_IDS_PATHS = {
  prod: 'https://rate-metrics.prodoctorov.ru/hits/update-counters/',
  stage: 'https://rate-metrics-stage.prodoctorov.com/hits/update-counters/',
}

const TIME_IN_VIEWPORT_MS = 2000
const INTERSECTION_OBSERVER_OPTIONS = {
  rootMargin: '-100px 0px',
}

function getUnsentIdsByType(type: string) {
  return [...VIEWED_IDS[type]].filter(viewedId => !SENT_IDS[type].includes(viewedId))
}

function sendViewedReviews() {
  const unsentLpuIds = getUnsentIdsByType(REVIEW_OBJECT_TYPES.lpu)
  const unsentDoctorIds = getUnsentIdsByType(REVIEW_OBJECT_TYPES.doctor)

  if (!unsentLpuIds.length && !unsentDoctorIds.length) {
    return
  }

  const formData = new FormData()

  formData.append('rates_id', JSON.stringify({
    lpu_rates_id: unsentLpuIds,
    doctor_rates_id: unsentDoctorIds,
  }))

  const url = process.env.IS_PROD ? SEND_VIEWED_IDS_PATHS.prod : SEND_VIEWED_IDS_PATHS.stage
  const isSuccess = navigator.sendBeacon?.(url, formData)

  if (isSuccess) {
    SENT_IDS[REVIEW_OBJECT_TYPES.lpu].push(...unsentLpuIds)
    SENT_IDS[REVIEW_OBJECT_TYPES.doctor].push(...unsentDoctorIds)
  }
}

function observeReviews(wrapper: HTMLElement, observer: IntersectionObserver) {
  const cards = [...wrapper.querySelectorAll<HTMLElement>('.lpu-review-card')]

  cards.forEach(card => {
    const reviewId = Number(card.dataset.reviewId)
    const reviewType = card.dataset.reviewType || REVIEW_OBJECT_TYPES.lpu
    const isDeleted = card.dataset.reviewIsDeleted === 'true'
    const body = card.querySelector('.lpu-review-card__body')

    if (!reviewId || !body || isDeleted || VIEWED_IDS[reviewType]?.has(reviewId)) {
      return
    }

    observer.observe(body)
  })
}

/**
 * Учёт просмотров карточек для метрик рейтинга.
 *
 * Карточка считается просмотренной после 2 с в зоне видимости;
 * `rootMargin` отсекает шапку, чтобы не считать касание верхнего края.
 * Удалённые отзывы не наблюдаются.
 *
 * Отправка — `sendBeacon` при уходе со страницы,
 * чтобы счётчик не терялся на закрытии вкладки.
 * Уже отправленные id помечаются,
 * чтобы `visibilitychange` и `pagehide` не дублировали хит.
 * На SSR не вешается: нужен IntersectionObserver.
 */
export default function useReviewsViewTracking(
  wrapperRef: Ref<HTMLElement | null>,
  isExpanded: Ref<boolean>,
) {
  if (process.env.IS_SERVER) {
    return
  }

  let observer: IntersectionObserver | null = null
  const timeouts: Record<string, ReturnType<typeof setTimeout>> = {}

  function createObserver() {
    return new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const reviewCardBody = entry.target as HTMLElement
        const reviewCard = reviewCardBody.closest<HTMLElement>('.lpu-review-card')
        const reviewId = Number(reviewCard?.dataset.reviewId)
        const reviewType = reviewCard?.dataset.reviewType || REVIEW_OBJECT_TYPES.lpu

        if (!reviewId || !reviewType) {
          return
        }

        if (VIEWED_IDS[reviewType]?.has(reviewId)) {
          observer?.unobserve(reviewCardBody)
          return
        }

        const timeoutId = `${reviewType}-${reviewId}`

        if (entry.isIntersecting) {
          timeouts[timeoutId] = setTimeout(() => {
            VIEWED_IDS[reviewType]?.add(reviewId)
            observer?.unobserve(reviewCardBody)
          }, TIME_IN_VIEWPORT_MS)
        } else {
          clearTimeout(timeouts[timeoutId])
        }
      })
    }, INTERSECTION_OBSERVER_OPTIONS)
  }

  function observe() {
    if (wrapperRef.value && observer) {
      observeReviews(wrapperRef.value, observer)
    }
  }

  onMounted(() => {
    observer = createObserver()
    observe()
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', sendViewedReviews)
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pagehide', sendViewedReviews)
  })

  watch(isExpanded, expanded => {
    if (expanded) {
      observe()
    }
  })
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    sendViewedReviews()
  }
}
